import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';

import type {
  ApiResponse,
  RoleEntity,
  PermissionEntity,
  CreateRoleDto,
  UpdateRoleDto,
  CreatePermissionDto,
  UpdatePermissionDto,
  RoleUsersResponse,
  ReplaceRoleUsersArg,
} from '../types/rbac.types';
import { userApi } from '../../users/apis/userApi';

type GetRolesArgs = {
  includeInactive?: boolean; // default true
};

export const rbacApi = createApi({
  reducerPath: 'rbacApi',
  baseQuery: customBaseQuery,
  tagTypes: ['Roles', 'RoleUsers', 'Permissions'],
  endpoints: (builder) => ({
    // ---------- ROLES ----------
    getRoles: builder.query<ApiResponse<RoleEntity[]>, GetRolesArgs | void>({
      query: (args) => {
        const includeInactive = args?.includeInactive ?? true; // ✅ default true
        return {
          url: '/rbac/roles',
          method: 'GET',
          params: { includeInactive },
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              { type: 'Roles' as const, id: 'LIST' },
              ...result.data.map((r) => ({ type: 'Roles' as const, id: r.id })),
            ]
          : [{ type: 'Roles' as const, id: 'LIST' }],
    }),

    getRoleById: builder.query<ApiResponse<RoleEntity>, number | string>({
      query: (id) => ({
        url: `/rbac/roles/${id}`,
        method: 'GET',
      }),
      providesTags: (_res, _err, id) => [{ type: 'Roles', id: String(id) }],
    }),

    getRoleUsers: builder.query<
      ApiResponse<RoleUsersResponse>,
      number | string
    >({
      query: (id) => ({
        url: `/rbac/roles/${id}/users`,
        method: 'GET',
      }),
      providesTags: (_res, _err, id) => [
        { type: 'RoleUsers', id: String(id) },
        { type: 'Roles', id: String(id) },
      ],
    }),

    replaceRoleUsers: builder.mutation<
      ApiResponse<RoleUsersResponse>,
      ReplaceRoleUsersArg
    >({
      query: ({ roleId, userIds }) => ({
        url: `/rbac/roles/${roleId}/users`,
        method: 'PUT',
        body: { userIds },
      }),
      invalidatesTags: (_res, _err, { roleId }) => [
        { type: 'RoleUsers', id: String(roleId) },
        { type: 'Roles', id: String(roleId) },
        { type: 'Roles', id: 'LIST' },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            userApi.util.invalidateTags([{ type: 'Users', id: 'LIST' }]),
          );
        } catch {
          // ignore
        }
      },
    }),

    createRole: builder.mutation<ApiResponse<RoleEntity>, CreateRoleDto>({
      query: (body) => ({
        url: '/rbac/roles',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Roles', id: 'LIST' }],
    }),

    updateRole: builder.mutation<
      ApiResponse<RoleEntity>,
      { id: number | string; body: UpdateRoleDto }
    >({
      query: ({ id, body }) => ({
        url: `/rbac/roles/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Roles', id: String(id) },
        { type: 'Roles', id: 'LIST' },
      ],
    }),

    deleteRole: builder.mutation<ApiResponse<RoleEntity>, number | string>({
      query: (id) => ({
        url: `/rbac/roles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'Roles', id: String(id) },
        { type: 'Roles', id: 'LIST' },
      ],
    }),

    // ---------- PERMISSIONS ----------
    getPermissions: builder.query<ApiResponse<PermissionEntity[]>, void>({
      query: () => ({
        url: '/rbac/permissions',
        method: 'GET',
      }),
      providesTags: (result) =>
        result?.data
          ? [
              { type: 'Permissions' as const, id: 'LIST' },
              ...result.data.map((p) => ({
                type: 'Permissions' as const,
                id: p.id,
              })),
            ]
          : [{ type: 'Permissions' as const, id: 'LIST' }],
    }),

    getPermissionById: builder.query<
      ApiResponse<PermissionEntity>,
      number | string
    >({
      query: (id) => ({
        url: `/rbac/permissions/${id}`,
        method: 'GET',
      }),
      providesTags: (_res, _err, id) => [
        { type: 'Permissions', id: String(id) },
      ],
    }),

    createPermission: builder.mutation<
      ApiResponse<PermissionEntity>,
      CreatePermissionDto
    >({
      query: (body) => ({
        url: '/rbac/permissions',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Permissions', id: 'LIST' },
        { type: 'Roles', id: 'LIST' },
      ],
    }),

    updatePermission: builder.mutation<
      ApiResponse<PermissionEntity>,
      { id: number | string; body: UpdatePermissionDto }
    >({
      query: ({ id, body }) => ({
        url: `/rbac/permissions/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Permissions', id: String(id) },
        { type: 'Permissions', id: 'LIST' },
        // roles may show permission descriptions in role details/pickers
        { type: 'Roles', id: 'LIST' },
      ],
    }),

    deletePermission: builder.mutation<
      ApiResponse<{ success: boolean }>,
      number | string
    >({
      query: (id) => ({
        url: `/rbac/permissions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'Permissions', id: String(id) },
        { type: 'Permissions', id: 'LIST' },
        { type: 'Roles', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  // roles
  useGetRolesQuery,
  useGetRoleByIdQuery,
  useGetRoleUsersQuery,
  useReplaceRoleUsersMutation,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,

  // permissions
  useGetPermissionsQuery,
  useGetPermissionByIdQuery,
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
  useDeletePermissionMutation,
} = rbacApi;
